import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import UnitReleaseRAApi from './unitReleaseRAApi';
import SelectionPanel from './selectionPanel';
import SelectedUnitsGrid from './selectedUnitsGrid';
import SavePanel from './savePanel';
import { extractDropdownArray, toOption, isApiSuccess, unwrapApiError, mergeCandidates, formatDateForApi } from './unitReleaseRAHelpers';

const rowKey = (row) => `${row.propertyNo}||${row.unitNo}||${row.unitRefNo}`;

const blankUnitInfo = () => ({ status: '', unitClass: '', groupId: '', leaseNo: '', isRenewedLease: false });

const UnitReleaseRAPage = () => {
  const api = new UnitReleaseRAApi();
  const navigate = useNavigate();

  const [propertyOptions, setPropertyOptions] = useState([]);
  const [releaseTypeOptions, setReleaseTypeOptions] = useState([]);
  const [canMakeRenewedActive, setCanMakeRenewedActive] = useState(false);

  const [date, setDate] = useState(new Date());
  const [releaseType, setReleaseType] = useState(null);
  const [property, setProperty] = useState(null);
  const [unitNo, setUnitNo] = useState(null);
  const [unitOptions, setUnitOptions] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [leaseReservedOnly, setLeaseReservedOnly] = useState(false);
  const [addEntireGroup, setAddEntireGroup] = useState(false);
  const [unitInfo, setUnitInfo] = useState(blankUnitInfo());

  const [selectedUnits, setSelectedUnits] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [makeRenewedActive, setMakeRenewedActive] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null); // { body, onConfirm }

  const releaseTypeLocked = selectedUnits.length > 0;

  useEffect(() => {
    const load = async () => {
      try {
        const [dropdownsRes, settingsRes] = await Promise.all([api.getDropdowns(['Properties', 'ReleaseTypes']), api.getSettings()]);
        const dropdowns = dropdownsRes?.data?.dropdowns || dropdownsRes?.data?.result?.dropdowns || {};
        setPropertyOptions(extractDropdownArray(dropdowns, 'Properties').map(toOption));
        setReleaseTypeOptions(extractDropdownArray(dropdowns, 'ReleaseTypes').map(toOption));
        const settings = settingsRes?.data?.result || {};
        setCanMakeRenewedActive(!!settings.canMakeRenewedActive);
      } catch (error) {
        console.error('[UnitReleaseRA] Error loading dropdowns/settings:', error);
        toast.error('Failed to load page data. Please refresh.');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (selectedUnits.length === 0) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [selectedUnits.length]);

  const loadUnits = async (propertyValue, leaseReserved) => {
    if (!propertyValue) {
      setUnitOptions([]);
      return;
    }
    setLoadingUnits(true);
    try {
      const res = await api.getUnits(propertyValue, leaseReserved);
      const units = res?.data?.result || [];
      setUnitOptions(units.map((u) => ({ value: u, label: u?.unitNo })));
    } catch (error) {
      console.error('[UnitReleaseRA] Error loading units:', error);
      setUnitOptions([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const handlePropertyChange = (opt) => {
    setProperty(opt);
    setUnitNo(null);
    setUnitInfo(blankUnitInfo());
    setAddEntireGroup(false);
    // property options come from toOption(), which stores the raw propertyNo
    // directly as `value` (not the whole dropdown item) - unlike the unit
    // options below, which store the whole unit object.
    loadUnits(opt?.value, leaseReservedOnly);
  };

  const handleLeaseReservedToggle = (checked) => {
    setLeaseReservedOnly(checked);
    setUnitNo(null);
    setUnitInfo(blankUnitInfo());
    loadUnits(property?.value, checked);
  };

  const handleUnitChange = async (opt) => {
    setUnitNo(opt);
    setAddEntireGroup(false);
    if (!opt) {
      setUnitInfo(blankUnitInfo());
      return;
    }
    try {
      const res = await api.getUnitInfo(property?.value, opt?.value?.unitNo, leaseReservedOnly);
      // A no-access unit is signaled by the request throwing (404, caught
      // below), not by a resolved response - hasAccess is left as an extra
      // guard in case the backend ever returns it as a flag on a 200 instead.
      if (!res?.data?.result?.hasAccess) {
        toast.error('Unit not found or you don\'t have access to it.');
        setUnitInfo(blankUnitInfo());
        return;
      }
      const info = res?.data?.result || {};
      setUnitInfo({
        status: info.status || '',
        unitClass: info.unitClass || '',
        groupId: info.groupId || '',
        leaseNo: info.leaseNo && info.leaseNo !== '0' ? info.leaseNo : '',
        isRenewedLease: !!info.isRenewedLease,
      });
    } catch (error) {
      if (error?.response?.status === 404) {
        toast.error('Unit not found or you don\'t have access to it.');
      } else {
        console.error('[UnitReleaseRA] Error loading unit info:', error);
        toast.error('Something went wrong. Please try again.');
      }
      setUnitInfo(blankUnitInfo());
    }
  };

  const appendCandidates = (units, latestCanMakeRenewedActive) => {
    setSelectedUnits((prev) => mergeCandidates(prev, units));
    if (typeof latestCanMakeRenewedActive === 'boolean') {
      setCanMakeRenewedActive(latestCanMakeRenewedActive);
    }
  };

  const runAdd = async () => {
    try {
      const res = await api.getCandidates({
        propertyNo: property?.value,
        unitNo: unitNo?.value?.unitNo,
        releaseType: releaseType?.label,
        entireGroup: addEntireGroup,
        groupId: unitInfo.groupId,
        leaseReserved: leaseReservedOnly,
      });
      if (res?.status === 204) {
        toast.info('No units matched.');
        return;
      }
      if (!isApiSuccess(res?.data)) {
        toast.error(unwrapApiError(res?.data));
        return;
      }
      const result = res?.data?.result || {};
      if (result.blockedMessage) {
        toast.error(result.blockedMessage);
        return;
      }
      if (result.confirmMessage) {
        setConfirm({
          body: result.confirmMessage,
          onConfirm: () => appendCandidates(result.units, result.canMakeRenewedActive),
        });
        return;
      }
      if (!result.units || result.units.length === 0) {
        toast.warning(res?.data?.message || 'No units matched.');
        return;
      }
      appendCandidates(result.units, result.canMakeRenewedActive);
    } catch (error) {
      if (error?.response?.status === 400) {
        toast.error(error?.response?.data?.message || 'Add failed.');
      } else if (error?.response?.status === 404) {
        toast.error('Unit not found or you don\'t have access to it.');
      } else {
        console.error('[UnitReleaseRA] Error fetching candidates:', error);
        toast.error('Something went wrong. Please try again.');
      }
    }
  };

  const handleAdd = () => {
    if (!property || !unitNo) {
      toast.warning('Select Property / Unit');
      return;
    }
    if (!releaseType) {
      toast.warning('Select Release Type');
      return;
    }
    if (unitInfo.isRenewedLease) {
      setConfirm({ body: 'This is a Renewed Lease, are you sure you want to continue?', onConfirm: runAdd });
      return;
    }
    runAdd();
  };

  const handleReset = () => {
    const doReset = () => {
      setProperty(null);
      setUnitNo(null);
      setUnitOptions([]);
      setUnitInfo(blankUnitInfo());
      setLeaseReservedOnly(false);
      setAddEntireGroup(false);
      setSelectedUnits([]);
      setReleaseType(null);
    };
    if (selectedUnits.length > 0) {
      setConfirm({ body: 'Clear the selected units?', onConfirm: doReset });
    } else {
      doReset();
    }
  };

  const handleRemoveRow = (key) => {
    setSelectedUnits((prev) => prev.filter((r) => rowKey(r) !== key));
  };

  const handleToggleRow = (key, checked) => {
    setSelectedUnits((prev) => prev.map((r) => (rowKey(r) === key ? { ...r, selected: checked } : r)));
  };

  const doSave = async () => {
    const items = selectedUnits.filter((r) => r.selected && !r.hasReceipt);
    setSaving(true);
    setSaveError('');
    try {
      const res = await api.releaseUnits({
        releaseDate: formatDateForApi(date),
        releaseType: releaseType?.label,
        remarks: remarks.trim(),
        makeRenewedActive: canMakeRenewedActive ? makeRenewedActive : false,
        items: items.map((r) => ({
          propertyNo: r.propertyNo,
          unitNo: r.unitNo,
          unitRefNo: r.unitRefNo,
          groupId: r.groupId,
          resvNo: r.resvNo,
        })),
      });
      if (!isApiSuccess(res?.data)) {
        setSaveError(unwrapApiError(res?.data));
        return;
      }
      const result = res?.data?.result || {};
      toast.success(`Release was successful. ${result.unitsReleased ?? items.length} unit(s) released.`);
      navigate('/Customer/Property/Master/UnitReleaseRA/Listing');
    } catch (error) {
      if (error?.response?.status === 400) {
        setSaveError(error?.response?.data?.message || 'Save failed.');
      } else if (error?.response?.status === 404) {
        setSaveError('Unit not found or you don\'t have access to it.');
      } else {
        console.error('[UnitReleaseRA] Error releasing units:', error);
        setSaveError('Something went wrong. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const ticked = selectedUnits.filter((r) => r.selected && !r.hasReceipt);
    if (ticked.length === 0) {
      setSaveError('Select Unit.');
      return;
    }
    if (!releaseType) {
      setSaveError('Select Release Type.');
      return;
    }
    if (!remarks.trim() || remarks.trim().length > 500) {
      setSaveError('Enter Remarks.');
      return;
    }
    if (!date) {
      setSaveError('Select a Date.');
      return;
    }
    setSaveError('');
    setConfirm({
      body: `Release ${ticked.length} unit(s) as '${releaseType?.label}'? This cannot be undone.`,
      onConfirm: doSave,
    });
  };

  const handleCancel = () => {
    if (selectedUnits.length > 0) {
      setConfirm({ body: 'Discard this release and leave the page?', onConfirm: () => navigate('/Customer/Property/Master/UnitReleaseRA/Listing') });
    } else {
      navigate('/Customer/Property/Master/UnitReleaseRA/Listing');
    }
  };

  const handleConfirmYes = () => {
    const action = confirm?.onConfirm;
    setConfirm(null);
    if (action) action();
  };

  return (
    <div className="p-3">
      <h5 className="mainInvHead mb-3">Unit Release</h5>

      <SelectionPanel
        date={date}
        onDateChange={setDate}
        releaseTypeOptions={releaseTypeOptions}
        releaseType={releaseType}
        onReleaseTypeChange={setReleaseType}
        releaseTypeLocked={releaseTypeLocked}
        propertyOptions={propertyOptions}
        property={property}
        onPropertyChange={handlePropertyChange}
        unitOptions={unitOptions}
        loadingUnits={loadingUnits}
        unitNo={unitNo}
        onUnitChange={handleUnitChange}
        unitRefNo={unitNo?.value?.unitRefNo}
        leaseReservedOnly={leaseReservedOnly}
        onToggleLeaseReserved={handleLeaseReservedToggle}
        addEntireGroup={addEntireGroup}
        onToggleEntireGroup={setAddEntireGroup}
        groupId={unitInfo.groupId}
        status={unitInfo.status}
        unitClass={unitInfo.unitClass}
        leaseNo={unitInfo.leaseNo}
        isRenewedLease={unitInfo.isRenewedLease}
        canAdd={!!(property && unitNo)}
        onAdd={handleAdd}
        onReset={handleReset}
      />

      <SelectedUnitsGrid rows={selectedUnits} onToggleRow={handleToggleRow} onRemoveRow={handleRemoveRow} />

      <SavePanel
        remarks={remarks}
        onRemarksChange={setRemarks}
        showRenewedCheckbox={canMakeRenewedActive}
        makeRenewedActive={makeRenewedActive}
        onToggleRenewedActive={setMakeRenewedActive}
        errorMessage={saveError}
        saving={saving}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <Modal show={!!confirm} onHide={() => setConfirm(null)} centered>
        <Modal.Body className="text-center p-4">
          <p className="mb-4">{confirm?.body}</p>
          <div className="d-flex justify-content-center gap-3">
            <button type="button" className="btn btn-primary px-4" onClick={handleConfirmYes}>
              Yes
            </button>
            <button type="button" className="btn btn-secondary px-4" onClick={() => setConfirm(null)}>
              No
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {saving && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
          }}
        >
          <ClipLoader color="#ffffff" size={40} />
          <div style={{ color: '#fff', fontSize: 18 }}>Releasing units, please wait…</div>
        </div>
      )}
    </div>
  );
};

export default UnitReleaseRAPage;
